import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Translation } from 'react-i18next';

import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { mdiClose } from '@mdi/js';
import Icon from '@mdi/react';

import Refresher from 'components/Refresher/Refresher';
import InTime from 'components/InTime/InTime';

const useStyles = makeStyles((theme) => ({
  root: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  // Header row mirrors the Projects-page mobile list drawer header
  // (src/scenes/Projects/Projects.js): title on the left, close on the right.
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(1),
  },
  drawerTitle: {
    ...theme.typography.fontPanton,
    fontSize: '0.95rem',
  },
  drawerCloseButton: {
    ...theme.component.projectsListButton,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.75),
    minWidth: theme.spacing(4),
    minHeight: theme.spacing(4),
  },
  // Clock section styled like the source list's search/actions section
  // (ProjectsActions root): dashboard paper with padding and a bottom margin.
  paper: {
    ...theme.component.dashboardPaper,
    boxSizing: 'border-box',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  clockSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  clock: {
    ...theme.typography.fontPantonLight,
    alignSelf: 'flex-start',
    color: theme.palette.text.tertiary,
    // Monospace digits so the ticking time stays fixed-width and doesn't jump
    // (the Panton font has no tabular figures, so tabular-nums has no effect).
    fontFamily: "'Roboto Mono', 'Menlo', 'Consolas', monospace",
    fontVariantNumeric: 'tabular-nums',
  },
  date: {
    ...theme.typography.fontPantonLight,
    alignSelf: 'flex-start',
    color: theme.palette.text.tertiary,
    marginBottom: theme.spacing(1),
    textTransform: 'capitalize',
  },
  today: {
    ...theme.typography.fontPantonLight,
    alignSelf: 'flex-end',
    color: theme.palette.text.caption,
    fontSize: '0.65rem',
  },
  inTime: {
    ...theme.typography.fontPantonSemiBold,
    alignSelf: 'flex-end',
    color: theme.palette.text.secondary,
    fontSize: '2.5rem',
    // Monospace digits so the running timer stays fixed-width and doesn't jump
    // (the Panton font has no tabular figures, so tabular-nums has no effect).
    fontFamily: "'Roboto Mono', 'Menlo', 'Consolas', monospace",
    fontVariantNumeric: 'tabular-nums',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    paddingBottom: theme.spacing(3),
  },
}));

function TimerContent(props) {
  const classes = useStyles();
  const { children, title, onClose } = props;

  return (
    <Translation ns={['common']}>
      {(t) => (
        <div className={classes.root}>
          <div className={classes.drawerHeader}>
            <Typography className={classes.drawerTitle}>{title}</Typography>
            <IconButton
              className={classes.drawerCloseButton}
              onClick={onClose}
              aria-label={t('common.close')}
            >
              <Icon path={mdiClose} size={0.9} />
            </IconButton>
          </div>
          <Paper className={classes.paper} elevation={2}>
            <div className={classes.clockSection}>
              <Refresher
                render={() => (
                  <>
                    <Typography className={classes.clock} variant="inherit">
                      {moment().format('HH:mm:ss')}
                    </Typography>
                    <Typography className={classes.date} variant="inherit">
                      {moment().format('dd Do MMMM YYYY')}
                    </Typography>
                  </>
                )}
              />
              <Typography className={classes.today} variant="caption">
                {t('datetime.today')}
              </Typography>
              <Typography className={classes.inTime} variant="inherit">
                <InTime full={true} />
              </Typography>
            </div>
          </Paper>
          <div className={classes.content}>{children}</div>
        </div>
      )}
    </Translation>
  );
}

TimerContent.defaultProps = {
  classes: {},
  children: [],
  title: '',
  onClose: () => null,
};

TimerContent.propTypes = {
  classes: PropTypes.object.isRequired,
  children: PropTypes.array.isRequired,
  title: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default TimerContent;
